import type {
  ReceiptAddress, ReceiptData, ReceiptPrintOptions, ReceiptRenderModel, TotalsKey,
} from '@matix/types';
import { renderReceiptDesignHtml } from '@matix/types';
import {
  escapeHtml, formatMoney, formatAmount, formatQty, formatEntryDate, pickName,
  entryDateParts, formatDateDmy, formatDateYmd, formatTimeFull,
} from './format.js';
import { formatReceiptAddress } from './formatReceiptAddress.js';
import { headerPromotions, promotionLabel, splitNamedDiscount } from './promotionLines.js';
import { buildA4Html } from './buildA4Html.js';
import { receiptZatcaQrSvg } from './zatcaReceipt.js';
import { buildA4FlowFreshHtml, usesFlowFreshA4 } from './buildA4FlowFreshHtml.js';
import { buildA4DesignModel } from './buildA4DesignModel.js';
import { renderA4DesignHtml } from '@matix/types';
import { usesBilingualReceipt, bothScripts } from './bilingualReceipt.js';
import { orderTypeNameEn, orderTypeNameAr } from './orderTypeName.js';

/**
 * The delivery address broken out one token per field, for a design that
 * writes its own labels.
 *
 * Both scripts are stored on the row and the slip's language picks between
 * them — the same rule `formatReceiptAddress` applies to the joined line. An
 * unfilled field resolves to '' (the legacy table stores those as `''` as
 * often as NULL), which is what lets a design wrap it in an
 * `[[optional segment]]` and drop its label with it.
 */
function addressFields(
  addr: ReceiptAddress | null | undefined,
  arabic: boolean,
): Record<string, string> {
  const pick = (en?: string | null, ar?: string | null): string => {
    const a = (ar ?? '').trim();
    const e = (en ?? '').trim();
    return arabic ? a || e : e || a;
  };
  const plain = (v?: string | null): string => (v ?? '').trim();
  return {
    addr_city: pick(addr?.city, addr?.cityAr),
    addr_district: pick(addr?.district, addr?.districtAr),
    addr_block: plain(addr?.block),
    addr_street: plain(addr?.street),
    addr_avenue: plain(addr?.avenue),
    addr_house: plain(addr?.house),
    addr_floor: plain(addr?.floor),
    addr_flat: plain(addr?.flat),
    addr_note: plain(addr?.note),
  };
}

/**
 * Print-option defaults, applied when the backend hasn't shipped printOptions
 * (older fixtures / tokens). Mirrors the built-in receipt's historical
 * behaviour: unit price + bilingual names on, everything else off, one copy,
 * Latin-script labels.
 */
const DEFAULT_PRINT_OPTIONS: ReceiptPrintOptions = {
  printUnitPrice: true,
  showItemBarcode: false,
  bilingualItemName: true,
  showItemCode: false,
  copies: 1,
  showPriceWithTax: false,
  printCustomOptions: false,
  arabic: false,
  a4: false,
};

function printOptionsOf(data: ReceiptData): ReceiptPrintOptions {
  return data.printConfig.printOptions ?? DEFAULT_PRINT_OPTIONS;
}

/**
 * Static receipt labels, localized when the receipt prints in Arabic.
 *
 * EVERY word the template puts on the paper is in here. An Arabic receipt with
 * an English word left on it does not read as "one untranslated label" to the
 * customer holding it — it reads as a broken receipt, and the words most likely
 * to be left behind (`REPRINT`, `NET TO COLLECT`, `CR:`) are exactly the ones
 * that matter when something has gone wrong with the sale. If you add a literal
 * to the template, add it here first.
 */
interface ReceiptLabels {
  receipt: string; payment: string; date: string; cashier: string;
  /** The promised delivery time row — delivery orders only. */
  deliveryTime: string;
  table: string; customer: string; phone: string; address: string;
  account: string; credit: string;
  subtotal: string; discount: string; delivery: string; tax: string; total: string;
  /** The line's own figure after its own discount. */
  lineTotal: string;
  /** Who sold the line — printed under the item when the line carries one. */
  salesman: string;
  cashTendered: string; change: string; pricesInclTax: string;
  /** Header identity lines. */
  crNo: string; taxNo: string;
  /** Reprint stripe. */
  reprint: string;
  /** Exchange / return document. */
  invoice: string; refund: string; sold: string; returned: string;
  netToCollect: string; netToRefund: string;
}

/**
 * The label set for THIS receipt: its own language, or — on a bilingual tenant
 * — both, the receipt's own language leading. Every `receiptLabels` call site
 * goes through here so a bilingual tenant cannot end up with one block of the
 * slip translated and the next one not.
 */
function receiptLabelsOf(data: ReceiptData): ReceiptLabels {
  const arabic = receiptArabic(data);
  if (!usesBilingualReceipt(data.printConfig)) return receiptLabels(arabic);
  const primary = receiptLabels(arabic);
  const secondary = receiptLabels(!arabic);
  const merged = {} as Record<keyof ReceiptLabels, string>;
  for (const key of Object.keys(primary) as Array<keyof ReceiptLabels>) {
    merged[key] = bothScripts(primary[key], secondary[key]);
  }
  return merged as ReceiptLabels;
}

/**
 * A name that exists in two scripts, resolved for THIS receipt: the receipt's
 * own language, or both on a bilingual tenant.
 *
 * `pickName` alone prefers Arabic whatever the receipt's language — harmless
 * for a company name, wrong for anything a bilingual slip has to lead with.
 */
function nameOf(data: ReceiptData, en: string | null | undefined, ar: string | null | undefined): string {
  if (!usesBilingualReceipt(data.printConfig)) return pickName(en, ar);
  return receiptArabic(data) ? bothScripts(ar, en) : bothScripts(en, ar);
}

/**
 * The same name in the OTHER script, for the line printed under `printed`.
 *
 * Empty when the tenant filled only one of the two columns, or when both
 * columns hold the same text — a name that exists once must never print
 * twice, the duplication this repo has already been bitten by on item names.
 * `branch_name_ar` cannot do this job: it falls back to the English name, so
 * pairing it with `branch_name` prints an English-only tenant's branch twice.
 */
function otherScriptName(
  printed: string,
  en: string | null | undefined,
  ar: string | null | undefined,
): string {
  const e = (en ?? '').trim();
  const a = (ar ?? '').trim();
  const other = printed === a ? e : a;
  return other && other !== printed ? other : '';
}

function receiptLabels(arabic: boolean): ReceiptLabels {
  if (arabic) {
    return {
      receipt: 'إيصال', payment: 'الدفع', date: 'التاريخ', cashier: 'الكاشير',
      deliveryTime: 'وقت التوصيل',
      table: 'طاولة', customer: 'العميل', phone: 'الهاتف', address: 'العنوان',
      account: 'الحساب', credit: 'آجل',
      subtotal: 'الإجمالي الفرعي', discount: 'الخصم', delivery: 'رسوم التوصيل',
      tax: 'الضريبة', total: 'الإجمالي', lineTotal: 'إجمالي الصنف',
      salesman: 'المندوب',
      cashTendered: 'المبلغ المدفوع', change: 'الباقي', pricesInclTax: 'الأسعار شاملة الضريبة',
      crNo: 'س.ت', taxNo: 'الرقم الضريبي',
      reprint: 'إعادة طباعة',
      invoice: 'فاتورة', refund: 'مرتجع', sold: 'المبيعات', returned: 'المرتجع',
      netToCollect: 'المطلوب تحصيله', netToRefund: 'المبلغ المسترد',
    };
  }
  return {
    receipt: 'Receipt', payment: 'Payment', date: 'Date', cashier: 'Cashier',
    deliveryTime: 'Delivery time',
    table: 'Table', customer: 'Customer', phone: 'Phone', address: 'Address',
    account: 'Account', credit: 'Credit',
    subtotal: 'Subtotal', discount: 'Discount', delivery: 'Delivery', tax: 'Tax', total: 'TOTAL',
    lineTotal: 'Item total',
    salesman: 'Salesman',
    cashTendered: 'Cash tendered', change: 'Change', pricesInclTax: 'Prices include tax',
    crNo: 'CR', taxNo: 'TAX',
    reprint: 'REPRINT',
    invoice: 'Invoice', refund: 'Refund', sold: 'Sold', returned: 'Returned',
    netToCollect: 'NET TO COLLECT', netToRefund: 'NET TO REFUND',
  };
}

/**
 * Inline CSS for the receipt iframe document.
 *
 * Design choices:
 *  - 80 mm thermal paper assumed (the Egyptian/Gulf default; 58 mm is a Phase
 *    5c knob, not Phase 5b).
 *  - The page is sized to the head's IMAGEABLE band (72 mm = 576 dots at
 *    203 dpi), not to the 80 mm of paper. A thermal printer silently CLIPS
 *    anything outside that band — it never scales to fit — and the casualty is
 *    always the right-hand column: the receipt number printed as "#20", the
 *    date as "2026-07-30 04:34 P", amounts as "0.136 KW".
 *    Laying out on 80 mm and reserving the difference as padding does not fix
 *    that reliably: it assumes the driver maps our page 1:1 onto the paper
 *    starting at dot 0, and drivers disagree (some inset the band; plenty of
 *    "80 mm" rolls measure 76). Making the PAGE the band leaves nothing to
 *    clip, whatever the driver does with the leftover paper. It is also what
 *    the server-side raster receipt has always done (rasterCanvas.ts is
 *    576 dots wide).
 *  - `@page { size: 72mm auto; margin: 0; }` also stops Chromium padding the
 *    ticket out to A4 and printing a tiny ribbon in the middle of a sheet.
 *  - Font stack favours Arabic-capable system fonts that ship on every
 *    cashier OS: Tahoma (Windows), Geeza Pro (macOS), Noto Sans Arabic
 *    (Linux/Android). No web fonts — they'd add latency and risk the dreaded
 *    "Arabic squares" if the network blips at print time.
 *  - Numbers stay LTR even inside an RTL block via `unicode-bidi: plaintext`
 *    on `.amount` — keeps "150.00 EGP" readable instead of "EGP 00.150".
 *  - `.row` uses flex/justify-between so amount-vs-label rows render correctly
 *    in either RTL or LTR document direction.
 */
const RECEIPT_CSS = `
  @page { size: 72mm auto; margin: 0; }
  * { box-sizing: border-box; }
  html, body {
    margin: 0;
    padding: 0;
    width: 72mm;
    background: white;
    color: #000;
    font-family: Tahoma, "Geeza Pro", "Noto Sans Arabic", Arial, sans-serif;
    font-size: 11px;
    line-height: 1.35;
  }
  /* A symmetric breathing margin INSIDE the 72 mm band, so glyphs don't sit
     on the first and last dot columns. Content spans 3mm..69mm. */
  body { padding: 4mm 3mm; }
  .center { text-align: center; }
  .receipt-logo {
    display: block;
    margin: 0 auto 4px;
    max-width: 60mm;
    max-height: 30mm;
    object-fit: contain;
  }
  .strong { font-weight: 700; }
  .lg { font-size: 14px; }
  .sm { font-size: 10px; color: #222; }
  /* The till's Notes box under the branch name. pre-wrap, because the
     cashier types it into a textarea and their line breaks are part of what
     they wrote; overflow-wrap anywhere, because a pasted tracking number has
     no spaces to break on and would otherwise run off the 72 mm band. */
  .header-note { white-space: pre-wrap; overflow-wrap: anywhere; }
  .muted { color: #555; }
  .divider {
    border-top: 1px dashed #000;
    margin: 4px 0;
  }
  .row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 6px;
  }
  /* The label must be allowed to shrink AND wrap. Arabic labels are much
     longer than their English equivalents (e.g. the Arabic for "Subtotal" is
     roughly twice as wide); with a non-shrinking label the row overflowed the
     paper width, and under RTL that overflow clipped off the LEFT edge — which
     is why the Arabic receipt looked broken while the English one was fine.
     min-width:0 (defeating the default min-width:auto on flex items) lets both
     cells shrink so the row always fits the page width in either direction. */
  .row > .label { flex: 0 1 auto; min-width: 0; overflow-wrap: anywhere; }
  .row > .value { flex: 1 1 auto; min-width: 0; text-align: end; overflow-wrap: anywhere; }
  .amount { unicode-bidi: plaintext; white-space: nowrap; }
  /* Both name lines are pinned to the LEFT edge, not to the page's start
     edge: on an Arabic (RTL) receipt "start" is the right, which used to put
     the Arabic name on the opposite side of the paper from the English one it
     belongs to. An explicit "left" (rather than relying on each div's own dir)
     also survives any inherited text-align. */
  .item-name { font-weight: 600; word-break: break-word; text-align: left; }
  .item-name-ar { font-weight: 600; text-align: left; }
  .item-line {
    margin-top: 2px;
    padding-bottom: 1px;
    /* Never tear one item across a page break: its name lines, sub-lines and
       math row belong together on the same side of the cut. (This template
       has no repeating table header to worry about — it lays items out as
       blocks, not as a <table> with a head.) */
    break-inside: avoid;
    page-break-inside: avoid;
  }
  /* Returned lines are identified by the Refund section heading, not by
     struck-through text (per ops: a strikethrough is not wanted). */
  .item-sub { padding: 0 0 1px; word-break: break-word; }
  .incl-tax-note { text-align: center; font-size: 10px; color: #555; margin-top: 2px; }
  .ex-section-head {
    margin-top: 5px;
    padding: 2px 0;
    border-top: 1px dashed #000;
    border-bottom: 1px dashed #000;
    font-weight: 700;
    text-align: center;
    letter-spacing: 0.04em;
  }
  .totals .row { padding: 1px 0; }
  .totals .row.total {
    margin-top: 2px;
    padding-top: 3px;
    border-top: 1px solid #000;
    font-weight: 700;
    font-size: 12px;
  }
  .footer-html { margin-top: 4px; font-size: 10px; }
  .footer-html * { max-width: 100%; }
  /* The ERP issue counter — bare, bold and centred, as the legacy slip has it. */
  .print-count { text-align: center; font-weight: 700; margin-top: 4px; }
  .reprint-stripe {
    text-align: center;
    border: 1px dashed #000;
    padding: 2px 0;
    margin: 0 0 4px 0;
    font-weight: 700;
    letter-spacing: 2px;
  }
`;

/**
 * Split a server-formatted table line into the term and the number.
 *
 * `"Table: 7"` → the localized word for table + `"7"`, because "Table" is the
 * generic default this package is entitled to translate. `"Sun bed: 5"` → the
 * tenant's own word, kept verbatim, + `"5"`. Anything that does not look like
 * `term: value` at all (an older payload, a hand-set label) is passed through
 * whole as the value with the localized term as the label — never dropped.
 *
 * The cashier's Receipts screen has its own copy of this split for the same
 * reason; the two are independent by design (that one has no receipt labels).
 */
function splitTableLabel(raw: string, L: ReceiptLabels): { term: string; value: string } {
  const at = raw.indexOf(':');
  if (at <= 0) return { term: L.table, value: raw.trim() };
  const term = raw.slice(0, at).trim();
  const value = raw.slice(at + 1).trim();
  if (!value) return { term: L.table, value: raw.trim() };
  return { term: term.toLowerCase() === 'table' ? L.table : term, value };
}

/**
 * Build the section showing the receipt header (company + branch identity).
 *
 * Receipt-friendly facts: a thermal ticket has no room for full bilingual
 * pairs of every line — we show ONE language per line (Arabic preferred via
 * `pickName`) and fall back gracefully when only one variant exists. The one
 * exception is a tenant on the bilingual receipt (`usesBilingualReceipt`),
 * which has chosen to spend that paper: `nameOf` then prints both.
 */
function buildHeaderHtml(data: ReceiptData): string {
  const { printConfig } = data;
  const L = receiptLabelsOf(data);
  const companyName = nameOf(data, printConfig.companyNameEn, printConfig.companyNameAr);
  const branchName = nameOf(data, printConfig.branchNameEn, printConfig.branchNameAr);
  const address = nameOf(data, printConfig.companyAddressEn, printConfig.companyAddressAr);
  const contact = nameOf(data, printConfig.companyContactEn, printConfig.companyContactAr);

  const lines: string[] = [];
  // Tenant logo at the very top, when configured. The src is a base64 data-URL
  // (pos_branding.ReceiptLogo) so it prints without a network fetch.
  if (printConfig.receiptLogo) {
    lines.push(`<img class="receipt-logo" src="${escapeHtml(printConfig.receiptLogo)}" alt="" />`);
  }
  if (companyName) lines.push(`<div class="center strong lg">${escapeHtml(companyName)}</div>`);
  if (branchName) lines.push(`<div class="center">${escapeHtml(branchName)}</div>`);
  // The branch name in the other script, on its own line under the first, so
  // the slip names the branch the way both of its customers read it. A tenant
  // that filled only one column prints the one line it printed before, and the
  // bilingual receipt already joins both into `branchName` — it adds nothing
  // there rather than saying the same thing twice.
  const branchNameAlt = usesBilingualReceipt(printConfig)
    ? ''
    : otherScriptName(branchName, printConfig.branchNameEn, printConfig.branchNameAr);
  if (branchNameAlt) lines.push(`<div class="center">${escapeHtml(branchNameAlt)}</div>`);
  if (printConfig.branchAddressLine) {
    lines.push(`<div class="center sm">${escapeHtml(printConfig.branchAddressLine)}</div>`);
  } else if (address) {
    lines.push(`<div class="center sm">${escapeHtml(address)}</div>`);
  }
  // The till's "Notes" box — the same free text the A4 sheet puts under
  // الملاحظات — under the whole block that names the shop: both branch names
  // AND the address line.
  //
  // It sat directly under the branch name until a tenant printed one and found
  // it wedged between two lines they both read as the branch. Their branch has
  // no Arabic name at all (`mstr_Branch.Name_AR` is a copy of `Name`), and the
  // Arabic line beneath the note was the company ADDRESS — which prints here
  // whenever the branch has no street of its own, and which for that tenant is
  // the district the branch is named after. Under the address, the note can no
  // longer land inside the shop's own lines whatever those columns hold.
  //
  // The cashier's words print verbatim in whatever script they typed, line
  // breaks kept. A sale with an empty box (nearly every sale) prints exactly
  // the header it printed before the box existed.
  const headerNote = (data.notes ?? '').trim();
  if (headerNote) {
    lines.push(`<div class="center header-note">${escapeHtml(headerNote)}</div>`);
  }
  if (contact) lines.push(`<div class="center sm">${escapeHtml(contact)}</div>`);
  // The number itself is Latin digits in both languages — a tax authority reads
  // it, not a customer — so only the prefix is localized, and it is kept out of
  // the `bdi` of the number so the two do not reorder around each other.
  if (printConfig.registerNo) {
    lines.push(`<div class="center sm muted">${escapeHtml(L.crNo)}: <bdi>${escapeHtml(printConfig.registerNo)}</bdi></div>`);
  }
  if (printConfig.taxRegisterNo) {
    lines.push(`<div class="center sm muted">${escapeHtml(L.taxNo)}: <bdi>${escapeHtml(printConfig.taxRegisterNo)}</bdi></div>`);
  }
  return lines.join('\n');
}

/**
 * Order metadata block: receipt number, date, cashier, customer.
 *
 * Uses `<div class="row">` (flex justify-between) so the label/value pairs
 * stay justified across the 80 mm width regardless of document direction.
 */
function buildMetaHtml(data: ReceiptData): string {
  const number = data.orderCode ?? data.orderId;
  const cashierLabel = cashierNameOf(data) || `#${data.cashier.id}`;
  const L = receiptLabelsOf(data);

  const rows: string[] = [
    `<div class="row"><span class="label">${escapeHtml(L.receipt)}</span><span class="value strong">#${escapeHtml(number)}</span></div>`,
  ];

  if (data.payments.length > 0) {
    const paymentLabel = data.payments
      .map((p) => nameOf(data, p.typeName, p.typeNameAr) || p.typeName)
      .filter((label) => label && label.length > 0)
      .join(' + ');
    if (paymentLabel) {
      rows.push(
        `<div class="row"><span class="label">${escapeHtml(L.payment)}</span><span class="value">${escapeHtml(paymentLabel)}</span></div>`
      );
    }
  }

  rows.push(
    `<div class="row"><span class="label">${escapeHtml(L.date)}</span><span class="value">${escapeHtml(formatEntryDate(data.entryDate, receiptArabic(data)))}</span></div>`,
    `<div class="row"><span class="label">${escapeHtml(L.cashier)}</span><span class="value">${escapeHtml(cashierLabel)}</span></div>`
  );

  // Delivery time — only a delivery order carries one. Bold, right under the
  // order date and in the same 12-hour format, so the customer and the driver
  // read "when" without hunting for it.
  if (data.deliveryDate && data.deliveryDate.trim() !== '') {
    rows.push(
      `<div class="row strong"><span class="label">${escapeHtml(L.deliveryTime)}</span><span class="value">${escapeHtml(formatEntryDate(data.deliveryDate, receiptArabic(data)))}</span></div>`
    );
  }

  // Table row — only when the cart had a table attached (dine-in).
  //
  // The server bakes the tenant's own terminology into `tableLabel`
  // ("Sun bed: 5" for villashams, "Table: 7" everywhere else), which used to
  // print beside our own label and read "Table  Table: 7" — and on an Arabic
  // receipt "طاولة  Table: 7", the term both duplicated and untranslated.
  //
  // So the term and the number are split apart. A term that is just the
  // generic "Table" is OURS to translate and the row uses the localized label;
  // a tenant that renamed it has said what they call the thing, in their own
  // words, and that wins over any translation we could offer.
  if (data.tableLabel) {
    const split = splitTableLabel(data.tableLabel, L);
    rows.push(
      `<div class="row strong"><span class="label">${escapeHtml(split.term)}</span><span class="value">${escapeHtml(split.value)}</span></div>`
    );
  }

  if (data.customer) {
    const customerName = nameOf(data, data.customer.name, data.customer.nameAr);
    if (customerName) {
      rows.push(
        `<div class="row"><span class="label">${escapeHtml(L.customer)}</span><span class="value">${escapeHtml(customerName)}</span></div>`
      );
    }
    if (data.customer.phone) {
      rows.push(
        `<div class="row"><span class="label">${escapeHtml(L.phone)}</span><span class="value amount">${escapeHtml(data.customer.phone)}</span></div>`
      );
    }
    // Where the order went. The structured delivery row when the sale had one,
    // otherwise the customer's own stored address; a credit customer has only
    // the free-text line `GL_Accounts` carries, so that is the last fallback.
    //
    // It prints on its own lines rather than beside the label because a full
    // address does not fit the value column of a 72 mm ticket — laid out as a
    // justified row it wraps mid-word and reads as noise.
    // On a bilingual tenant the address is rendered twice — the city and the
    // district are catalogue rows with both scripts (GL_Governorate /
    // GL_Area), and a driver who reads one of them should not have to guess.
    // The second pass is dropped when it comes back identical, which is what
    // an address with no Arabic names does.
    const addressLines = formatReceiptAddress(data.customer.addressDetail, receiptArabic(data));
    if (usesBilingualReceipt(data.printConfig)) {
      const other = formatReceiptAddress(data.customer.addressDetail, !receiptArabic(data));
      if (other.length > 0 && other.join('\n') !== addressLines.join('\n')) {
        addressLines.push(...other);
      }
    }
    const freeText = (data.customer.address ?? '').trim();
    const printedAddress = addressLines.length > 0 ? addressLines : freeText ? [freeText] : [];
    if (printedAddress.length > 0) {
      rows.push(
        `<div class="row"><span class="label">${escapeHtml(L.address)}</span>` +
          `<span class="value">${printedAddress.map((l) => escapeHtml(l)).join('<br>')}</span></div>`
      );
    }
    if (data.customer.type === 'credit') {
      rows.push(`<div class="row sm muted"><span class="label">${escapeHtml(L.account)}</span><span class="value">${escapeHtml(L.credit)}</span></div>`);
    }
  }

  return rows.join('\n');
}

/**
 * One block per item — the bilingual name(s) on their own line(s), then the
 * math (qty × price = total) on a justified row underneath. We don't try to fit
 * name+qty+price+total on a single 80 mm line; long item names wrap and the
 * math row stays tidy.
 *
 * Both the English and Arabic names are printed when the tenant maintains a
 * bilingual catalog: English on its own line, Arabic on the line directly
 * below. When only one variant exists (or the two are identical) we print just
 * the one to avoid a blank/duplicate line on thermal paper.
 *
 * BOTH lines are `dir="ltr"`, so the Arabic name starts at the same left edge
 * as the English one and the pair reads as a single block. The Arabic line used
 * to be `dir="rtl"`, which parked it against the right edge — on an Arabic
 * (RTL) receipt the two names ended up on opposite sides of the paper and the
 * eye had to jump across to pair them up. `dir` only sets the paragraph
 * direction; the Arabic glyphs themselves still shape and read right-to-left
 * inside the run, which is what makes this safe.
 */
type CurrencyArg = string | { code: string; precision?: number };

function buildItemNameHtml(
  item: ReceiptData['items'][number],
  bilingual: boolean,
  arabic: boolean,
): string {
  const en = typeof item.name === 'string' ? item.name.trim() : '';
  const ar = typeof item.nameAr === 'string' ? item.nameAr.trim() : '';

  // The receipt's own language LEADS, and the other script follows underneath
  // when the tenant prints bilingual names. On an Arabic receipt, an English
  // name on the first line and the Arabic one demoted below it is the same
  // untranslated-receipt problem as an English label — the customer's eye goes
  // to the first line. The A4 sheet has always led with the language's name;
  // this brings the ticket into line.
  const primary = arabic ? ar : en;
  const secondary = arabic ? en : ar;
  const primaryClass = arabic ? 'item-name item-name-ar' : 'item-name';
  const secondaryClass = arabic ? 'item-name' : 'item-name item-name-ar';

  const lines: string[] = [];
  if (primary) {
    lines.push(`  <div class="${primaryClass}" dir="ltr">${escapeHtml(primary)}</div>`);
  }
  // The other script prints on its own line when it exists, isn't a duplicate,
  // AND the tenant prints bilingual item names. With no name in the receipt's
  // own language, it becomes the sole name line regardless — a receipt must
  // never name an item with nothing at all.
  if (secondary && secondary !== primary && (bilingual || !primary)) {
    lines.push(`  <div class="${secondaryClass}" dir="ltr">${escapeHtml(secondary)}</div>`);
  }
  // Neither variant present — emit a single empty name line so the math row
  // still has something above it (defensive; upstream always sends a name).
  if (lines.length === 0) {
    lines.push(`  <div class="item-name">${escapeHtml(item.name)}</div>`);
  }
  return lines.join('\n');
}

function renderItemLine(
  item: ReceiptData['items'][number],
  currency: CurrencyArg,
  po: ReceiptPrintOptions,
  L: ReceiptLabels,
  arabic: boolean,
  /** Bilingual tenant: print the item's other script even if the tenant's own
   *  BilingualItemName option is off — that flag is about paper length, and
   *  this receipt has already chosen to spend it. */
  bilingual: boolean,
): string {
  const qty = formatQty(Math.abs(item.qty));
  const price = formatMoney(item.price, currency);
  const hasDiscount = item.discount != null && item.discount > 0;

  /*
   * A discounted line prints THREE figures, in the order the customer reads
   * them: what the goods cost, what came off, what is left.
   *
   * The math row therefore shows the GROSS — qty × the undiscounted unit price
   * — not the net. Printing the net there and a discount row underneath it was
   * a slip that did not add up: a 28.00 haircut discounted 2.80 showed
   * "×1  25.20" then "Discount −2.80", inviting the customer to compute 22.40.
   * The net now closes the block on its own labelled row.
   */
  const sign = item.total < 0 ? -1 : 1;
  const grossValue = hasDiscount ? item.total + sign * (item.discount as number) : item.total;
  const gross = formatMoney(grossValue, currency);
  const total = formatMoney(item.total, currency);
  const discount = hasDiscount
    ? `<div class="row sm muted"><span class="label">${escapeHtml(L.discount)}</span><span class="value amount">-${escapeHtml(formatMoney(item.discount as number, currency))}</span></div>`
    : '';
  // Only worth a row when something was taken off — an undiscounted line's
  // math row already IS its total, and repeating it would be noise on paper
  // that is charged by the millimetre.
  const netRow = hasDiscount
    ? `<div class="row sm"><span class="label">${escapeHtml(L.lineTotal)}</span><span class="value amount">${escapeHtml(total)}</span></div>`
    : '';

  // The math row: with unit price it reads "qty × price = gross"; without it
  // (IsPrintUnitPriceInReceipt off) it collapses to "×qty = gross".
  // A multi-unit item names its unit right after the quantity — "2 CTN ×
  // 2.400" — in the receipt's own language. Lines without one (every item
  // sold in a single unit) print exactly as before.
  const unitLabel = (arabic ? item.unitAr || item.unit : item.unit || item.unitAr)?.trim();
  const qtyLabel = unitLabel ? `${qty} ${unitLabel}` : qty;
  const mathLabel = po.printUnitPrice
    ? `${escapeHtml(qtyLabel)} × ${escapeHtml(price)}`
    : `×${escapeHtml(qtyLabel)}`;

  // Optional item sub-lines. Code (item number) and barcode each print when
  // their flag is on; modifiers + note both ride the one IsPrintCustomOptions
  // flag.
  const subLines: string[] = [];
  if (po.showItemCode && item.code) {
    subLines.push(`  <div class="row sm muted"><span class="label">#${escapeHtml(item.code)}</span><span class="value"></span></div>`);
  }
  if (po.showItemBarcode && item.barcode) {
    subLines.push(`  <div class="row sm muted"><span class="label">${escapeHtml(item.barcode)}</span><span class="value"></span></div>`);
  }
  if (po.printCustomOptions && item.mods && item.mods.length > 0) {
    subLines.push(`  <div class="item-sub sm muted">${escapeHtml(item.mods.join(' · '))}</div>`);
  }
  if (po.printCustomOptions && item.note) {
    subLines.push(`  <div class="item-sub sm muted">📝 ${escapeHtml(item.note)}</div>`);
  }
  // Who sold it — not behind any print flag: a line that carries a salesman
  // names them on the customer's copy, in the slip's own script (both on a
  // bilingual tenant), falling back to whichever script the user has.
  const salesman = bilingual
    ? (arabic ? bothScripts(item.salesmanAr, item.salesman) : bothScripts(item.salesman, item.salesmanAr))
    : (arabic ? item.salesmanAr || item.salesman : item.salesman || item.salesmanAr);
  if (salesman) {
    subLines.push(`  <div class="row sm muted"><span class="label">${escapeHtml(L.salesman)}</span><span class="value">${escapeHtml(salesman)}</span></div>`);
  }

  return [
    `<div class="item-line${item.isReturn ? ' item-line-return' : ''}">`,
    buildItemNameHtml(item, po.bilingualItemName || bilingual, arabic),
    ...subLines,
    `  <div class="row sm"><span class="label">${mathLabel}</span><span class="value amount">${escapeHtml(gross)}</span></div>`,
    `  ${discount}`,
    `  ${netRow}`,
    '</div>',
  ].join('\n');
}

/** Net row label on a return/exchange — positive net is collected from the customer. */
function exchangeNetLabel(netDifference: number, L: ReceiptLabels): string {
  return netDifference >= 0 ? L.netToCollect : L.netToRefund;
}

function buildItemsHtml(data: ReceiptData, currency: CurrencyArg): string {
  const po = printOptionsOf(data);
  const arabic = receiptArabic(data);
  const bilingual = usesBilingualReceipt(data.printConfig);
  const L = receiptLabelsOf(data);
  // Exchange/return: group into an Invoice (sold) section and a Refund
  // (returned) section, each with its own heading + entry number, mirroring
  // the legacy exchange printout. The sales invoice prints first, with the
  // return details below it.
  if (data.exchange) {
    const returned = data.items.filter((i) => i.isReturn);
    const sold = data.items.filter((i) => !i.isReturn);
    const sections: string[] = [];
    if (sold.length > 0) {
      const no = data.exchange.saleEntryNo != null ? ` #${data.exchange.saleEntryNo}` : '';
      // One language, the receipt's own. These headings used to be bilingual
      // ("Invoice #470 — فاتورة") from the days when the ticket had no language
      // of its own to follow; now that it does, printing both is the same
      // half-translated slip the customer complained about.
      sections.push(
        `<div class="ex-section-head">${escapeHtml(L.invoice)}${escapeHtml(no)}</div>`,
        ...sold.map((it) => renderItemLine(it, currency, po, L, arabic, bilingual)),
      );
    }
    if (returned.length > 0) {
      const no = data.exchange.returnEntryNo != null ? ` #${data.exchange.returnEntryNo}` : '';
      sections.push(
        `<div class="ex-section-head">${escapeHtml(L.refund)}${escapeHtml(no)}</div>`,
        ...returned.map((it) => renderItemLine(it, currency, po, L, arabic, bilingual)),
      );
    }
    return sections.join('\n');
  }
  return data.items.map((item) => renderItemLine(item, currency, po, L, arabic, bilingual)).join('\n');
}

/**
 * Subtotal / discount / tax / total. Discount and tax rows are suppressed when
 * zero — keeping the receipt as compact as possible on thermal paper.
 */
function buildTotalsHtml(data: ReceiptData, currency: CurrencyArg): string {
  // Exchange/return: show the two legs and the net settled, matching the
  // legacy "Exchange Net" document.
  if (data.exchange) {
    const ex = data.exchange;
    const XL = receiptLabelsOf(data);
    const netLabel = exchangeNetLabel(ex.netDifference, XL);
    const rows: string[] = [];
    if (ex.soldTotal > 0) {
      rows.push(
        `<div class="row"><span class="label">${escapeHtml(XL.sold)}</span><span class="value amount">${escapeHtml(formatMoney(ex.soldTotal, currency))}</span></div>`,
      );
    }
    if (ex.returnedTotal > 0) {
      rows.push(
        `<div class="row"><span class="label">${escapeHtml(XL.returned)}</span><span class="value amount">-${escapeHtml(formatMoney(ex.returnedTotal, currency))}</span></div>`,
      );
    }
    rows.push(
      `<div class="row total"><span class="label">${escapeHtml(netLabel)}</span><span class="value amount">${escapeHtml(formatMoney(Math.abs(ex.netDifference), currency))}</span></div>`,
    );
    return `<div class="totals">\n${rows.join('\n')}\n</div>`;
  }

  const po = printOptionsOf(data);
  const L = receiptLabelsOf(data);
  const rows: string[] = [
    `<div class="row"><span class="label">${escapeHtml(L.subtotal)}</span><span class="value amount">${escapeHtml(formatMoney(data.totals.subtotal, currency))}</span></div>`,
  ];
  // The header carries ONE discount figure, and a promotion is a named part of
  // it — not a further deduction. Printing the whole figure and then the named
  // part underneath showed "-10.00" twice on a receipt that was only ever 10.00
  // off, and a customer reading the column could not make it add up.
  //
  // So the named parts are printed as ordinary rows and the generic "Discount"
  // row carries only what is left unnamed — the cashier's own hand discount, a
  // coupon, redeemed points. Together they equal `totals.discount` exactly, and
  // every number on the paper counts once:
  //
  //     Subtotal                        85.40
  //     [TEST] Free delivery over 50   -10.00
  //     Delivery                        10.00
  //     TOTAL                           85.40
  //
  const named = splitNamedDiscount(data, receiptArabic(data));
  if (named.unnamed > 0) {
    rows.push(
      `<div class="row"><span class="label">${escapeHtml(L.discount)}</span><span class="value amount">-${escapeHtml(formatMoney(named.unnamed, currency))}</span></div>`
    );
  }
  for (const promo of named.promotions) {
    rows.push(
      `<div class="row"><span class="label">${escapeHtml(promo.label)}</span><span class="value amount">-${escapeHtml(formatMoney(promo.amount, currency))}</span></div>`
    );
  }
  // The delivery fee is its own header column, not part of the subtotal, so
  // without this row the arithmetic on the paper does not close: a free
  // delivery promotion reads as a discount that changed nothing. Printed after
  // the discount and before tax, which is the order the net is computed in.
  if ((data.totals.deliveryFee ?? 0) > 0) {
    rows.push(
      `<div class="row"><span class="label">${escapeHtml(L.delivery)}</span><span class="value amount">${escapeHtml(formatMoney(data.totals.deliveryFee!, currency))}</span></div>`
    );
  }
  if (data.totals.tax > 0) {
    rows.push(
      `<div class="row"><span class="label">${escapeHtml(L.tax)}</span><span class="value amount">${escapeHtml(formatMoney(data.totals.tax, currency))}</span></div>`
    );
  }
  rows.push(
    `<div class="row total"><span class="label">${escapeHtml(L.total)}</span><span class="value amount">${escapeHtml(formatMoney(data.totals.total, currency))}</span></div>`
  );
  // "Prices inclusive of tax" note (ShowItemAmountWithTax). Line-level tax is
  // not available on the receipt payload, so we annotate the totals rather than
  // recompute per-line tax-inclusive amounts.
  const inclTaxNote =
    po.showPriceWithTax && data.totals.tax > 0
      ? `\n<div class="incl-tax-note">${escapeHtml(L.pricesInclTax)}</div>`
      : '';
  return `<div class="totals">\n${rows.join('\n')}\n</div>${inclTaxNote}`;
}

/**
 * Payments breakdown plus the change-due line if any. We always print every
 * split (even single-payment receipts get a one-row breakdown) so the
 * customer can verify what was charged.
 */
function buildPaymentsHtml(data: ReceiptData, currency: CurrencyArg): string {
  const L = receiptLabelsOf(data);
  const lines: string[] = data.payments.map((p) => {
    const label = nameOf(data, p.typeName, p.typeNameAr) || p.typeName;
    return `<div class="row"><span class="label">${escapeHtml(label)}</span><span class="value amount">${escapeHtml(formatMoney(p.amount, currency))}</span></div>`;
  });

  if (typeof data.cashTendered === 'number' && data.cashTendered > 0) {
    lines.push(
      `<div class="row sm muted"><span class="label">${escapeHtml(L.cashTendered)}</span><span class="value amount">${escapeHtml(formatMoney(data.cashTendered, currency))}</span></div>`
    );
  }
  if (typeof data.changeDue === 'number' && data.changeDue > 0) {
    lines.push(
      `<div class="row strong"><span class="label">${escapeHtml(L.change)}</span><span class="value amount">${escapeHtml(formatMoney(data.changeDue, currency))}</span></div>`
    );
  }
  return lines.join('\n');
}


/**
 * Does this receipt print in Arabic?
 *
 * The app's own UI language wins when the caller passes one: staff serving in
 * Arabic hand over an Arabic receipt without an admin flipping a tenant-wide
 * setting. Falls back to ArabiReset (`printOptions.arabic`) when no app
 * language is supplied — older callers, fixtures, and the server-side paths,
 * which have no cashier to ask.
 *
 * THIS NOW GOVERNS THE 72 mm TICKET TOO, not just the A4 sheet. It used to be
 * A4-only, on the reasoning that the ticket's layout is shared with the raster
 * path and with tenants who chose a fixed till-slip language. But a cashier
 * working in Arabic is handing an Arabic-speaking customer a slip, and having
 * to get an admin to flip a tenant-wide flag to make that happen was the wrong
 * shape of answer. A tenant that genuinely wants one fixed language on paper
 * still gets it: the fallback is unchanged, and every server-rendered receipt
 * (which sends no `language`) is byte-identical to before.
 */
/**
 * Who rang the sale up, in the script the slip is printed in.
 *
 * Deliberately NOT `nameOf`, which falls through to `pickName` — Arabic-first
 * regardless of language, because most values on these receipts exist in only
 * one script and preferring Arabic is the better guess. A person has BOTH
 * names filled in, so there is nothing to guess: an Arabic slip prints
 * `Security_Users.Name_AR` and an English one prints `.Name`, each falling
 * back to the other when the tenant left it blank. Same rule as item names
 * (buildItemNameHtml), which is the other field where both scripts are real.
 *
 * A bilingual receipt prints both, in the slip's own order, exactly as
 * `nameOf` does for the customer.
 */
function cashierNameOf(data: ReceiptData): string {
  const en = typeof data.cashier.name === 'string' ? data.cashier.name.trim() : '';
  const ar = typeof data.cashier.nameAr === 'string' ? data.cashier.nameAr.trim() : '';
  const arabic = receiptArabic(data);
  if (usesBilingualReceipt(data.printConfig)) {
    return arabic ? bothScripts(ar, en) : bothScripts(en, ar);
  }
  return arabic ? ar || en : en || ar;
}

function receiptArabic(data: ReceiptData): boolean {
  if (data.language) return data.language === 'ar';
  return printOptionsOf(data).arabic;
}

/**
 * Compose the final HTML document for the receipt.
 *
 * Output is a complete `<!DOCTYPE html>` document — designed to be written
 * straight into an iframe via `document.write`, NOT injected into the host
 * document. The footer-html block is inserted unescaped because it comes from
 * the trusted tenant-admin field; the iframe sandbox keeps any tenant-authored
 * scripts isolated from the cashier page.
 */
export function buildReceiptHtml(data: ReceiptData): string {
  // Currency comes from PrintConfig (server-resolved from
  // mstr_Subsidary_currency + mstr_Currencies). Legacy callers may still
  // pass a string `data.currency`; we honor that override first, fall back
  // to the printConfig.currency object, and finally to the EGP/2-decimal
  // default for the tiny set of pre-currency receipts in test fixtures.
  const currency = data.currency
    ?? data.printConfig.currency
    ?? { code: 'EGP', precision: 2 };

  const po = printOptionsOf(data);

  // A4 Print Designer: a sheet the tenant drew themselves. It is the first
  // thing consulted on the A4 path, and ONLY when enabled with a design —
  // every tenant without one falls through to exactly what they printed
  // before (FlowFresh's sheet, a receipt design re-laid on A4, or the default).
  const a4Custom = data.printConfig.a4Design;
  if (po.a4 && a4Custom?.enabled && a4Custom.design) {
    return renderA4DesignHtml(
      a4Custom.design,
      buildA4DesignModel(data, currency, po, receiptArabic(data)),
    );
  }

  // FlowFresh's A4 invoice is their own document, not a variation on ours, so
  // it is chosen before anything else on the A4 path — including the Receipt
  // Designer, whose block layout was never meant to produce a tax invoice.
  // The thermal ticket is untouched: this is the A4 sheet only.
  if (po.a4 && usesFlowFreshA4(data.printConfig)) {
    return buildA4FlowFreshHtml(data, currency, po, receiptArabic(data));
  }

  // Per-tenant custom design takes over when the admin has enabled one.
  // Otherwise we fall through to the built-in template below. With A4 on, the
  // design's blocks are laid out on a sheet instead of the thermal band — the
  // tenant keeps their design, on the paper they asked for.
  const custom = data.printConfig.receiptDesign;
  if (custom?.enabled && custom.design) {
    const model = buildRenderModel(data, currency, custom.design.page?.showCurrencyCode !== false);
    // On A4 the sheet's direction follows the app language too, so a design
    // whose page direction is 'auto' flips with it — and its labels follow the
    // same reader, the way the built-in A4 sheet's do.
    if (po.a4) {
      model.direction = receiptArabic(data) ? 'rtl' : 'ltr';
      model.arabicLabels = receiptArabic(data);
    }
    return renderReceiptDesignHtml(custom.design, model, po.a4 ? { paper: 'a4' } : {});
  }

  // PrintReceiptA4: a full tax invoice rather than a 72 mm ticket. Lives in its
  // own module — it shares the data but none of the ticket's layout.
  if (po.a4) return buildA4Html(data, currency, po, receiptArabic(data));

  // Receipt language / direction: the app's language when the caller supplied
  // one, otherwise the tenant's ArabiReset. Arabic prints RTL with Arabic
  // labels throughout; English prints LTR with Latin ones.
  const arabic = receiptArabic(data);
  const dir = arabic ? 'rtl' : 'ltr';
  const lang = arabic ? 'ar' : 'en';
  const L = receiptLabelsOf(data);

  // No REPRINT / إعادة طباعة stripe on a reprint: removed on request
  // (2026-09-29) in both languages — a reprinted slip is the same document.
  const footer = data.printConfig.receiptFooterHtml
    ? `<div class="divider"></div><div class="footer-html">${data.printConfig.receiptFooterHtml}</div>`
    : '';

  // The ERP's issue counter, printed the way the legacy Sismatix till prints
  // it: the bare number, bold and centred, last thing on the slip.
  //
  // ONLY above 1. The legacy slip shows the 1 as well, but every receipt this
  // fleet has ever printed has shown nothing there, and a lone "1" appearing
  // on every customer's paper is a change nobody asked for. Above 1 it is the
  // information the number exists to carry — this document was edited after it
  // was first handed over — and it appears exactly where the tenants who came
  // from the old till already read it.
  const printCountMark =
    (data.printCount ?? 0) > 1
      ? `<div class="divider"></div><div class="print-count">${escapeHtml(String(data.printCount))}</div>`
      : '';

  // The Saudi tax QR, on a tenant set up for ZATCA (VAT number + SAR) — see
  // zatcaReceipt.ts. Inline-styled rather than a RECEIPT_CSS rule, so every
  // other tenant's slip is byte for byte what it was.
  const zatcaSvg = receiptZatcaQrSvg(data);
  const zatcaQr = zatcaSvg
    ? `<div class="divider"></div><div style="text-align:center;margin-top:4px">${zatcaSvg}</div>`
    : '';

  const numberForTitle = data.orderCode ?? data.orderId;

  return `<!DOCTYPE html>
<html lang="${lang}" dir="${dir}">
<head>
<meta charset="utf-8">
<title>${escapeHtml(L.receipt)} #${escapeHtml(numberForTitle)}</title>
<style>${RECEIPT_CSS}</style>
</head>
<body>
${buildHeaderHtml(data)}
<div class="divider"></div>
${buildMetaHtml(data)}
<div class="divider"></div>
${buildItemsHtml(data, currency)}
<div class="divider"></div>
${buildTotalsHtml(data, currency)}
<div class="divider"></div>
${buildPaymentsHtml(data, currency)}
${zatcaQr}${footer}${printCountMark}
</body>
</html>`;
}

/**
 * Flatten a ReceiptData into the generic ReceiptRenderModel the shared
 * custom-design renderer consumes. All money/qty/date values are pre-formatted
 * here so the renderer stays presentation-only and matches the admin preview.
 */
function buildRenderModel(
  data: ReceiptData,
  currency: CurrencyArg,
  /**
   * The design's page-level choice about the currency code. Passed in rather
   * than read off the design here because this function builds the model for a
   * design it never sees — keeping it a parameter is what stops the two
   * drifting into disagreement about the same receipt.
   */
  showCurrencyCode = true,
): ReceiptRenderModel {
  const { printConfig: pc } = data;
  const money = (n: number): string =>
    showCurrencyCode ? formatMoney(n, currency) : formatAmount(n, currency);
  // The design's own labels are the tenant's words and are left alone; the
  // VALUES we generate — the datetime's meridiem, the exchange net label — are
  // ours, and follow the receipt's language like everything else.
  const arabic = receiptArabic(data);
  const L = receiptLabels(arabic);

  // Date / time: the backend usually sends a pre-formatted string; try to
  // parse it for a separate date + time, else fall back to the formatted form.
  const parsed = new Date(data.entryDate);
  const valid = !Number.isNaN(parsed.getTime());
  // The wall-clock pieces, parsed without going through the till's locale.
  const parts = entryDateParts(data.entryDate);
  const datetime = formatEntryDate(data.entryDate, arabic);

  const paidTotal = data.payments.reduce((s, p) => s + (p.amount || 0), 0);
  const currencyCode = typeof currency === 'string' ? currency : currency.code;

  const customerName = data.customer ? pickName(data.customer.name, data.customer.nameAr) : '';
  const ex = data.exchange;

  const fields: Record<string, string> = {
    order_no: String(data.orderCode ?? data.orderId),
    order_date: valid ? parsed.toLocaleDateString() : datetime,
    order_time: valid ? parsed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '',
    // The two above go through the browser's locale, so what they print
    // depends on how the till's OS is configured — fine for a design that just
    // wants "a date", useless for one reproducing a specific slip. These three
    // are parsed from the tenant wall-clock string and formatted here, so every
    // till in the fleet prints the same characters.
    order_date_dmy: parts ? formatDateDmy(parts) : '',
    order_date_ymd: parts ? formatDateYmd(parts) : '',
    order_time_full: parts ? formatTimeFull(parts, arabic) : '',
    datetime,
    // Both scripts, always — unlike the labels around them, which the tenant
    // typed in one language. A slip heading itself "Delivery/توصيل" needs the
    // pair on one line whatever the till is set to.
    order_type: orderTypeNameEn(data.orderType),
    order_type_ar: orderTypeNameAr(data.orderType),
    // What the customer paid with, as the splits name it. A multi-split sale
    // lists every method here; the `payments` block still prints the amounts.
    //
    // The name follows the SLIP's language, unlike `pickName`, which prefers
    // Arabic whatever is being printed — an English ticket reading
    // "Payment Type : نقدي" is the bug that rule causes.
    payment_type: data.payments
      .map((p) => (arabic ? p.typeNameAr || p.typeName : p.typeName || p.typeNameAr || ''))
      .map((n) => (n ?? '').trim())
      .filter((n) => n.length > 0)
      .join(' + '),
    // Same rule as the built-in layout: a Receipt-Designer slip is still a
    // customer receipt, so {{cashier_name}} follows its language too.
    cashier_name: cashierNameOf(data) || `#${data.cashier.id}`,
    customer_name: customerName ?? '',
    customer_phone: data.customer?.phone ?? '',
    // One line, not the built-in layout's three: a design places this token
    // wherever it likes, including inside a justified row, and a token that
    // could inject line breaks would tear that row apart.
    customer_address:
      formatReceiptAddress(data.customer?.addressDetail, arabic).join(' · ') ||
      (data.customer?.address ?? '').trim(),
    // The same address field by field, so a design can write the legacy slip's
    // own wording ("Block: 3 Street: 308 House: 4") instead of ours. Each is
    // the plain value — the labels belong to the design.
    ...addressFields(data.customer?.addressDetail, arabic),
    table: data.tableLabel ?? '',
    // The till's Notes box. Empty on a sale nobody noted anything on, so a
    // design binds it with `hideIfEmpty` and the block disappears — which is
    // what the starter design does, under the branch name.
    notes: (data.notes ?? '').trim(),
    // Empty on every non-delivery order, so a block bound to it with
    // `hideIfEmpty` only shows up on a delivery receipt.
    delivery_datetime: data.deliveryDate ? formatEntryDate(data.deliveryDate, arabic) : '',
    // The ERP's issue counter, as the legacy Sismatix slip prints it: the bare
    // number, no label, so a design reproducing that slip can put it exactly
    // where the tenant's customers already look for it. Always filled when
    // known, INCLUDING the 1 on an unedited receipt — the legacy till prints
    // the 1 too, and a design that wants the mark only on an edited copy binds
    // `print_count_edited` instead.
    print_count: data.printCount != null ? String(data.printCount) : '',
    // Empty on a receipt that has only ever been issued once, so a block bound
    // to it with `hideIfEmpty` appears only on one that was edited.
    print_count_edited: (data.printCount ?? 0) > 1 ? String(data.printCount) : '',
    // The latest edit: who and when. Empty on a receipt never edited, so a
    // block bound with `hideIfEmpty` appears only on an edited copy. The name
    // follows the slip's language, like the cashier's.
    edited_by: ((arabic ? data.editedByAr || data.editedBy : data.editedBy || data.editedByAr) ?? '').trim(),
    edited_at: data.editedAt ? formatEntryDate(data.editedAt, arabic) : '',
    company_name: pc.companyNameEn ?? pc.companyNameAr ?? '',
    company_name_ar: pc.companyNameAr ?? '',
    branch_name: pickName(pc.branchNameEn, pc.branchNameAr) ?? '',
    // `branch_name` goes through `pickName`, which prefers Arabic whatever the
    // slip's language — right for a bilingual header, wrong for a design whose
    // labels are all English and which needs "Arzak Branch", not "فرع الأرزاق".
    // These two say which script they want and never change with the till.
    branch_name_en: (pc.branchNameEn || pc.branchNameAr || '').trim(),
    branch_name_ar: (pc.branchNameAr || pc.branchNameEn || '').trim(),
    // The OTHER script's branch name — whichever of the two `branch_name` did
    // not print. Empty when the tenant has only one name, or both columns say
    // the same thing, so a design can pair it with `branch_name` under
    // `hideIfEmpty` and never print a branch twice. The two tokens above always
    // resolve to something and cannot be paired that way.
    branch_name_alt: otherScriptName(
      pickName(pc.branchNameEn, pc.branchNameAr) ?? '',
      pc.branchNameEn,
      pc.branchNameAr,
    ),
    branch_address: pc.branchAddressLine ?? '',
    company_address: pickName(pc.companyAddressEn, pc.companyAddressAr) ?? '',
    company_contact: pickName(pc.companyContactEn, pc.companyContactAr) ?? '',
    tax_number: pc.taxRegisterNo ?? '',
    tax_invoice_title_ar: (data.customer?.taxNumber ?? '').trim() ? 'فاتوره ضريبيه' : 'فاتوره ضريبيه مبسطه',
    cr_number: pc.registerNo ?? '',
    subtotal: money(data.totals.subtotal),
    discount: data.totals.discount > 0 ? money(data.totals.discount) : '',
    promotion_names: headerPromotions(data)
      .filter((p) => p.amount > 0)
      .map((p) => promotionLabel(p, arabic))
      .filter((n) => n.length > 0)
      .join(' + '),
    delivery: (data.totals.deliveryFee ?? 0) > 0 ? money(data.totals.deliveryFee!) : '',
    tax: data.totals.tax > 0 ? money(data.totals.tax) : '',
    total: money(data.totals.total),
    paid: money(paidTotal),
    change: data.changeDue && data.changeDue > 0 ? money(data.changeDue) : '',
    currency: currencyCode,
    // Return/exchange tokens — empty strings on a plain sale so a design can
    // bind them with `hideIfEmpty`.
    sold_total: ex && ex.soldTotal > 0 ? money(ex.soldTotal) : '',
    returned_total: ex && ex.returnedTotal > 0 ? money(ex.returnedTotal) : '',
    net_total: ex ? money(Math.abs(ex.netDifference)) : '',
    net_label: ex ? exchangeNetLabel(ex.netDifference, L) : '',
  };

  const totals: Partial<Record<TotalsKey, string>> = {
    subtotal: money(data.totals.subtotal),
    total: money(data.totals.total),
    paid: money(paidTotal),
  };
  // Named promotions take their own rows (see promotionLines.ts); the design's
  // Discount row keeps only the unnamed rest. No promotions = exactly as before.
  const named = splitNamedDiscount(data, arabic);
  if (named.promotions.length > 0) {
    if (named.unnamed > 0) totals.discount = money(named.unnamed);
  } else if (data.totals.discount > 0) {
    totals.discount = money(data.totals.discount);
  }
  // `delivery` has been a TotalsKey since the designer shipped; nothing ever
  // filled it, so a design binding that row printed a blank.
  if ((data.totals.deliveryFee ?? 0) > 0) totals.delivery = money(data.totals.deliveryFee!);
  if (data.totals.tax > 0) totals.tax = money(data.totals.tax);
  if (data.changeDue && data.changeDue > 0) totals.change = money(data.changeDue);

  const po = printOptionsOf(data);

  return {
    fields,
    items: data.items.map((it) => ({
      name: it.name,
      nameAr: it.nameAr ?? null,
      code: it.code ?? null,
      barcode: it.barcode ?? null,
      // Return lines carry a negative qty on the wire; the Refund section
      // heading already says they are returns, so print the magnitude (what
      // the built-in template does).
      qty: formatQty(Math.abs(it.qty)),
      // Resolved to the slip's language here, like every other name on a line —
      // the renderer is presentation-only and never picks a script.
      unit: (arabic ? it.unitAr || it.unit : it.unit || it.unitAr) ?? null,
      price: money(it.price),
      discount: it.discount && it.discount > 0 ? money(it.discount) : null,
      total: money(it.total),
      ...(it.mods && it.mods.length > 0 ? { mods: it.mods } : {}),
      note: it.note ?? null,
      ...(it.isReturn ? { isReturn: true } : {}),
    })),
    totals,
    ...(named.promotions.length > 0 && !ex
      ? { promotions: named.promotions.map((p) => ({ label: p.label, amount: money(p.amount) })) }
      : {}),
    // Present only on a return/exchange — makes the items block group into
    // Invoice + Refund sections and the totals block print the two legs + net.
    ...(ex
      ? {
          exchange: {
            saleEntryNo: ex.saleEntryNo != null ? String(ex.saleEntryNo) : null,
            returnEntryNo: ex.returnEntryNo != null ? String(ex.returnEntryNo) : null,
            soldTotal: ex.soldTotal > 0 ? money(ex.soldTotal) : null,
            returnedTotal: ex.returnedTotal > 0 ? money(ex.returnedTotal) : null,
            netLabel: exchangeNetLabel(ex.netDifference, L),
            netValue: money(Math.abs(ex.netDifference)),
          },
        }
      : {}),
    payments: data.payments.map((p) => ({
      label: pickName(p.typeName, p.typeNameAr) || p.typeName,
      amount: money(p.amount),
    })),
    changeValue: data.changeDue && data.changeDue > 0 ? money(data.changeDue) : null,
    logoDataUrl: pc.receiptLogo,
    // Set only on a tenant set up for ZATCA; the design's QR block (or the
    // one the renderer appends to a design without it) reads it.
    ...((): { zatcaQrSvg?: string } => {
      const qr = receiptZatcaQrSvg(data);
      return qr ? { zatcaQrSvg: qr } : {};
    })(),
    footerHtml: pc.receiptFooterHtml,
    // Direction follows the ArabiReset print option — NOT the app language,
    // unlike the built-in template above (page.direction: 'auto' in a design
    // resolves to this). A custom design is a layout the tenant drew, with
    // labels they typed in one language; flipping its direction from a till
    // toggle would move their blocks around and leave their own words facing
    // the wrong way.
    //
    // The same flag also translates those labels (`arabicLabels`). Direction
    // alone was never what the tenant ticking "Print receipt in Arabic"
    // meant — it gave them an RTL slip still captioned Receipt / Date /
    // Cashier, which reads as a broken receipt rather than an Arabic one.
    // Only words the shared dictionary knows move; anything else, including a
    // design already written in Arabic, prints exactly as typed. A tenant who wants a bilingual design draws two.
    // Print options are also passed through so the custom items block inherits
    // the tenant-wide barcode/code/note flags.
    direction: po.arabic ? 'rtl' : 'ltr',
    arabicLabels: po.arabic,
    printOptions: po,
  };
}
